import { renderToStaticMarkup } from 'react-dom/server';
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as L from 'leaflet';
import { LocateFixedIcon, MinusIcon, PlusIcon } from 'lucide-react';
import {
  fetchRoutePolyline,
  freshnessColor,
  getLocationFreshness,
} from '@/entities/order';
import { createOrderMap } from '../lib/osm-basemap';
import { MapButton } from './map-canvas';
import { CourierMarker, type ExtraMarker } from './courier-marker';

interface OsmCanvasProps {
  extraMarkers?: ExtraMarker[];
  points: { coords: [number, number]; type: string; order?: number }[];
  courierCoords?: [number, number] | undefined;
  courierLastReportedAt: string | null;
  className?: string | undefined;
}

const latLng = ([lng, lat]: [number, number]): L.LatLngTuple => [lat, lng];

function routePin(kind: string): HTMLElement {
  const origin = kind === 'origin';
  const pin = document.createElement('div');
  pin.style.cssText =
    'position:relative;width:36px;height:58px;display:flex;justify-content:center';
  const head = document.createElement('span');
  head.style.cssText = `position:absolute;top:0;display:grid;place-items:center;width:34px;height:34px;border:3px solid white;border-radius:50%;background:${origin ? '#ff7900' : '#23265b'};box-shadow:0 2px 6px #0002`;
  // Fixed SVG markup only: API values never enter the icon HTML.
  head.innerHTML = origin
    ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10V7l2-4h14l2 4v3M3 10c0 3 4 3 4 0 0 3 5 3 5 0 0 3 5 3 5 0 0 3 4 3 4 0M5 13v8h14v-8M9 21v-6h6v6"/></svg>'
    : '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 21V3h14l-4 5 4 5H5"/></svg>';
  const stem = document.createElement('span');
  stem.style.cssText = `position:absolute;top:34px;width:2px;height:20px;background:${origin ? '#ff7900' : '#23265b'};border-left:1px solid white`;
  const foot = document.createElement('span');
  foot.style.cssText = `position:absolute;bottom:0;width:8px;height:5px;border:1px solid ${origin ? '#ff7900' : '#23265b'};border-radius:50%;background:white`;
  pin.append(stem, foot, head);
  return pin;
}

export function OsmCanvas({
  points,
  extraMarkers = [],
  courierCoords,
  courierLastReportedAt,
  className,
}: OsmCanvasProps) {
  const { t } = useTranslation('orders');
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const routePoints = points.map((point) => point.coords);
  const orderedPoints = [...points].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const legs = orderedPoints
    .slice(0, -1)
    .map(
      (point, index) =>
        [point.coords, orderedPoints[index + 1]!.coords] as [number, number][],
    );
  const routeKey = legs.map((pair) => pair.map((c) => c.join(',')).join('>')).join('|');
  const { data: routes = [] } = useQuery({
    queryKey: ['order-route-legs', routeKey],
    queryFn: async () =>
      Promise.all(
        legs.map(async (coords) => {
          const snapped = await fetchRoutePolyline(coords);
          return snapped && snapped.length >= 2
            ? { coords: snapped, snapped: true }
            : { coords, snapped: false };
        }),
      ),
    enabled: legs.length > 0,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!container.current) return;
    const { map: instance, dispose } = createOrderMap(container.current);
    map.current = instance;
    return () => {
      dispose();
      map.current = null;
    };
  }, []);

  const geometryKey = JSON.stringify(routePoints);
  const markerKinds = `${Boolean(courierCoords)}:${extraMarkers.map((marker) => marker.kind).join(',')}`;
  const frameCoords = useRef<[number, number][]>([]);
  useEffect(() => {
    frameCoords.current = [
      ...routePoints,
      ...(courierCoords ? [courierCoords] : []),
      ...extraMarkers.map((marker) => marker.coords),
    ];
  }, [routePoints, courierCoords, extraMarkers]);
  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    const coords = frameCoords.current;
    if (coords.length)
      instance.fitBounds(L.latLngBounds(coords.map(latLng)), {
        padding: [40, 40],
        maxZoom: 16,
      });
  }, [geometryKey, markerKinds]);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    const layers = L.layerGroup().addTo(instance);
    points.forEach((point) => {
      L.marker(latLng(point.coords), {
        icon: L.divIcon({
          html: routePin(point.type),
          className: '',
          iconSize: [36, 58],
          iconAnchor: [18, 58],
        }),
      }).addTo(layers);
    });
    if (courierCoords) {
      const color = freshnessColor(getLocationFreshness(courierLastReportedAt));
      L.marker(latLng(courierCoords), {
        icon: L.divIcon({
          html: renderToStaticMarkup(
            <CourierMarker color={color} hint={t('detail.courier')} />,
          ),
          className: '',
          iconSize: [36, 36],
          iconAnchor: [18, 18],
        }),
      })
        .addTo(layers)
        .bindTooltip(t('detail.courier'));
    }
    const origin = orderedPoints.find((point) => point.type === 'origin')?.coords;
    if (courierCoords && origin)
      L.polyline([latLng(courierCoords), latLng(origin)], {
        color: '#7C3AED',
        weight: 3,
        dashArray: '7 7',
        opacity: 0.9,
      }).addTo(layers);
    extraMarkers.forEach((marker) => {
      const color = marker.kind === 'offer' ? '#7C3AED' : batteryColor(marker.batteryPct);
      const pin = L.marker(latLng(marker.coords), {
        icon: L.divIcon({
          html: renderToStaticMarkup(
            <CourierMarker
              color={color}
              battery={marker.kind === 'battery'}
              hint={
                marker.batteryPct == null
                  ? t('detail.batteryLocation')
                  : `${t('detail.batteryLocation')} · ${Math.round(marker.batteryPct)}%`
              }
            />,
          ),
          className: '',
          iconSize: [36, 36],
          iconAnchor: marker.kind === 'battery' ? [-8, 18] : [18, 18],
        }),
      }).addTo(layers);
      if (marker.kind === 'offer') {
        pin.bindTooltip(`COURIER_ID: ${marker.courierId}`);
        pin.on('click', () => {
          window.location.assign(`/couriers/${marker.courierId}`);
        });
        if (points[0])
          L.polyline([latLng(marker.coords), latLng(points[0].coords)], {
            color,
            weight: 3,
          }).addTo(layers);
      }
    });
    routes.forEach((route) =>
      L.polyline(route.coords.map(latLng), {
        color: '#2563EB',
        weight: 4,
        ...(route.snapped ? {} : { dashArray: '6 8', opacity: 0.55 }),
      }).addTo(layers),
    );
    return () => {
      layers.remove();
    };
  }, [points, courierCoords, courierLastReportedAt, extraMarkers, routes, t]);

  return (
    <div className={`relative isolate ${className ?? ''}`} data-testid="order-map-osm">
      <div ref={container} className="absolute inset-0" />
      <div className="absolute right-3 bottom-3 z-[1000] flex flex-col gap-1.5">
        <MapButton
          label={t('detail.fitToRoute')}
          onClick={() => {
            const coords = [
              ...routePoints,
              ...(courierCoords ? [courierCoords] : []),
              ...extraMarkers.map((marker) => marker.coords),
            ];
            if (coords.length)
              map.current?.fitBounds(L.latLngBounds(coords.map(latLng)), {
                padding: [40, 40],
                maxZoom: 16,
              });
          }}
        >
          <LocateFixedIcon className="size-4" />
        </MapButton>
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <MapButton
            label={t('map.zoomIn')}
            onClick={() => {
              map.current?.zoomIn();
            }}
            bare
          >
            <PlusIcon className="size-4" />
          </MapButton>
          <div className="h-px bg-border" />
          <MapButton
            label={t('map.zoomOut')}
            onClick={() => {
              map.current?.zoomOut();
            }}
            bare
          >
            <MinusIcon className="size-4" />
          </MapButton>
        </div>
      </div>
    </div>
  );
}

function batteryColor(pct: number | null | undefined) {
  if (pct == null || !Number.isFinite(pct)) return '#64748B';
  if (pct <= 15) return '#DC2626';
  if (pct <= 35) return '#D97706';
  return '#16A34A';
}
